/**
 * Client record (Ficha de Cliente) download — P16b step (c). The app generates the
 * record of one party of the agreement, pre-filled from that person's data, as a PDF
 * for the client to check and sign. The signed copy then goes back on file as a
 * "Client record" document (P23).
 *
 * Same raw same-origin fetch + Content-Disposition filename pattern as the annexes.
 */
import { downloadBlob } from './iaaExports';
import type { DocumentLanguage, UUID } from './types';

export { downloadBlob };

export async function downloadClientRecord(
  agreementId: UUID,
  personId: UUID,
  language: DocumentLanguage,
): Promise<{ blob: Blob; fileName: string }> {
  const res = await fetch(
    `/agreements/${agreementId}/client-record?person_id=${personId}&language=${language}`,
    { method: 'GET', credentials: 'include' },
  );
  if (!res.ok) {
    let detail: unknown = null;
    try { detail = (await res.json()).detail; } catch { /* non-JSON error body */ }
    throw new Error(typeof detail === 'string' ? detail : 'The client record could not be generated.');
  }
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const fileName = m ? decodeURIComponent(m[1]) : `client_record_${language}.pdf`;
  return { blob: await res.blob(), fileName };
}

/** A box of the uploaded form that could not be read as its field (S13). */
export interface ClientRecordUnread {
  field: string;
  /** Words for a block box ("Financial situation · Income · line 1 · Amount"); a plain
   *  person field comes back as its own name, which the page labels. */
  label: string;
  text: string;
  reason: string;
}

/** What a filled client record holds, in the Person API's shape (S13). */
export interface ClientRecordReadResult {
  kind: 'NATURAL' | 'LEGAL';
  language: 'PT' | 'EN' | null;
  values: Record<string, unknown>;
  unread: ClientRecordUnread[];
}

/** S13 (Pedro 2026-10-11): read a client record the client filled in on a computer. The
 *  server only reads it (nothing about the person changes, the file is not kept); the
 *  page shows the values for the user to tick and stage. */
export async function readFilledClientRecord(personId: UUID, file: File): Promise<ClientRecordReadResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/persons/${personId}/client-record/read`, {
    method: 'POST', credentials: 'include', body: form,
  });
  if (!res.ok) {
    let detail: unknown = null;
    try { detail = (await res.json()).detail; } catch { /* non-JSON error body */ }
    throw new Error(typeof detail === 'string' ? detail : 'The client record could not be read.');
  }
  return res.json();
}
